import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  clientCategoriesTransitions,
  type ClientCategory,
} from '../../_shared/domain/licence-client-categories';
import type { LicenceCsm } from '../../_shared/domain/licence-csm';
import type { AddLicenceError, LicenceDraftChange } from '../domain/licence-draft.model';
import { initialState } from './add-licence.state';

/**
 * Add Licence slice. Reducers are pure state transitions only; the create I/O
 * (the `POST /licences` call) lives in the saga, which dispatches these `save*`
 * transitions as it progresses. This is what lets the reducers be unit-tested as
 * `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'addLicence',
  initialState,
  reducers: {
    /**
     * A field changed; clears that field's validation error. When it is the
     * name, any availability verdict is stale the instant the text changes, and a
     * post-submit api error (e.g. the 409 duplicate) is about the previous value,
     * so both reset; picking a category or a country leaves the name's verdict
     * alone.
     */
    fieldChanged: (state, action: PayloadAction<LicenceDraftChange>) => {
      const change = action.payload;
      delete state.validationErrors[change.field];
      switch (change.field) {
        case 'name':
          state.draft.name = change.value;
          state.nameAvailability = 'idle';
          if (state.error?.kind === 'api') state.error = null;
          break;
        case 'client_category':
          state.draft.client_category = change.value;
          break;
        case 'based_in':
          state.draft.based_in = change.value;
          break;
        case 'csm_user_id':
          state.draft.csm_user_id = change.value;
          break;
      }
    },
    // ── Customer Success Manager candidates (#583, dispatched by the saga) ──
    csmCandidatesLoadStarted: (state) => {
      state.csmCandidates.status = 'loading';
    },
    csmCandidatesLoadSucceeded: (state, action: PayloadAction<LicenceCsm[]>) => {
      state.csmCandidates = { items: action.payload, status: 'loaded' };
    },
    /** The select keeps "None" usable: a CSM is optional, the licence can still be created. */
    csmCandidatesLoadFailed: (state) => {
      state.csmCandidates.status = 'failed';
    },
    // ── Client categories (#604, dispatched by the saga) ──
    clientCategoriesLoadStarted: (state) => {
      clientCategoriesTransitions.loadStarted(state.clientCategories);
    },
    clientCategoriesLoadSucceeded: (state, action: PayloadAction<ClientCategory[]>) => {
      clientCategoriesTransitions.loadSucceeded(state.clientCategories, action.payload);
    },
    clientCategoriesLoadFailed: (state) => {
      clientCategoriesTransitions.loadFailed(state.clientCategories);
    },
    clientCategoryAddStarted: (state) => {
      clientCategoriesTransitions.addStarted(state.clientCategories);
    },
    /** The new category joins the list and is picked; what the admin added it for. */
    clientCategoryAddSucceeded: (state, action: PayloadAction<ClientCategory>) => {
      clientCategoriesTransitions.addSucceeded(state.clientCategories, action.payload);
      state.draft.client_category = action.payload.value;
      delete state.validationErrors.client_category;
    },
    clientCategoryAddFailed: (state, action: PayloadAction<string>) => {
      clientCategoriesTransitions.addFailed(state.clientCategories, action.payload);
    },
    clientCategoryAddCleared: (state) => {
      clientCategoriesTransitions.addCleared(state.clientCategories);
    },
    // ── Live name-availability check (dispatched by the saga) ──
    nameCheckStarted: (state) => {
      state.nameAvailability = 'checking';
    },
    nameCheckSucceeded: (state, action: PayloadAction<boolean>) => {
      state.nameAvailability = action.payload ? 'taken' : 'available';
    },
    /** Fail-open: a broken probe never blocks creation; the backend 409 is the real guard. */
    nameCheckFailed: (state) => {
      state.nameAvailability = 'idle';
    },
    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state, action: PayloadAction<string>) => {
      state.saveStatus = 'succeeded';
      state.createdLicenceId = action.payload;
    },
    saveFailed: (state, action: PayloadAction<AddLicenceError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset to the pristine slice (after success / on unmount). */
    resetForm: () => initialState,
  },
});

export const addLicenceActions = slice.actions;
export const addLicenceReducer = slice.reducer;
